import { message } from 'antd';
import ZebraBrowserPrintWrapper from 'zebra-browser-print-wrapper-extended';

interface GetParamsProps<T> {
  filters: T;
}

export function getParams<T>(props: GetParamsProps<T>): URLSearchParams {
  const params = new URLSearchParams();

  if (props.filters) {
    for (const [key, value] of Object.entries(props.filters)) {
      if (value === undefined || value === null) {
        // undefined/null query'e eklenmez — aksi halde String(undefined)="undefined" gönderilir ve backend 400 döndürür
        continue;
      }
      if (Array.isArray(value)) {
        value.forEach((v) => {
          params.append(key, String(v));
        });
      } else if (typeof value === 'object') {
        // Object ise JSON.stringify ile çevir
        params.append(key, JSON.stringify(value));
      } else {
        params.append(key, String(value));
      }
    }
  }

  return params;
}

export function generateRandomString(length: number = 12): string {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  let result = '';

  for (let i = 0; i < length; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length));
  }

  return result;
}

export function genBarcode(suffix: string = ''): string {
  const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
  const charactersLength = characters.length;
  let randomString = '';
  for (let i = 0; i < 1; i++) {
    randomString += characters.charAt(Math.floor(Math.random() * charactersLength));
  }

  const date = new Date();
  const formattedDate =
    date.getFullYear().toString() + String(date.getMonth() + 1).padStart(2, '0') + String(date.getDate()).padStart(2, '0');

  const randomNum1 = Math.floor(Math.random() * 900) + 100;
  const randomNum2 = Math.floor(Math.random() * 900) + 100;
  const randomNum3 = Math.floor(Math.random() * 900) + 100;

  return `${suffix}${randomString}${formattedDate}${randomNum1}${randomNum2}${randomNum3}`;
}

export function btoaUtf8(input: string): string {
  // UTF-8 kodlamasına uygun hale getir
  const utf8Encoded = new TextEncoder().encode(input);
  // Her bir byte'ı Base64 stringine çevir
  return btoa(String.fromCharCode(...utf8Encoded));
}

export const printBarcode = async (zpl: string) => {
  try {
    const browserPrint = new ZebraBrowserPrintWrapper();
    const defaultPrinter = await browserPrint.getDefaultPrinter();
    browserPrint.setPrinter(defaultPrinter);
    // const printerStatus = await browserPrint.checkPrinterStatus();
    // if (printerStatus.isReadyToPrint) {
    browserPrint.print(zpl);
    // } else {
    // console.log('Error/s', printerStatus.errors);
    // }
  } catch (error) {
    console.error('Error/s', error);
  }
};

export async function sleep(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}
export function generatePassword(length: number = 12): string {
  const letters = 'abcdefghijkmnopqrstuvwxyzABCDEFGHJKLMNOPQRSTUVWXYZ';
  const digits = '0123456789';
  const specialChars = '!*+_';

  const allChars = letters + digits + specialChars;

  if (length < 8) {
    throw new Error('Parola uzunluğu en az 8 olmalıdır');
  }

  // Sunucu politikası (@IsStrongPassword): büyük + küçük harf + rakam + özel
  // karakter her zaman garanti (eskiden yalnız rakam + özel garanti ediliyordu)
  const lower = 'abcdefghijkmnopqrstuvwxyz';
  const upper = 'ABCDEFGHJKLMNOPQRSTUVWXYZ';
  let result = '';

  result += digits.charAt(Math.floor(Math.random() * digits.length));
  result += specialChars.charAt(Math.floor(Math.random() * specialChars.length));
  result += lower.charAt(Math.floor(Math.random() * lower.length));
  result += upper.charAt(Math.floor(Math.random() * upper.length));

  for (let i = result.length; i < length; i++) {
    result += allChars.charAt(Math.floor(Math.random() * allChars.length));
  }

  const shuffled = result
    .split('')
    .sort(() => 0.5 - Math.random())
    .join('');

  return shuffled;
}

export function genContainerBarcode(suffix: string = ''): string {
  // unittime + random 3 digit number
  const date = new Date();
  const unix = Math.floor(date.getTime() / 1000);
  const randomNum = Math.floor(Math.random() * 900) + 100;
  const randomNum2 = Math.floor(Math.random() * 900) + 100;

  return `${suffix}_${unix}-${randomNum}-${randomNum2}`;
}

export function validateTC(tc: string | number): boolean {
  if (typeof tc === 'number') {
    tc = String(tc);
  }

  if (!/^\d{11}$/.test(tc)) {
    return false;
  }

  if (tc[0] === '0') {
    return false;
  }

  const digits = tc.split('').map(Number);
  const totalOfFirst10 = digits.slice(0, 10).reduce((acc, digit) => acc + digit, 0);
  if (totalOfFirst10 % 10 !== digits[10]) {
    return false;
  }

  const oddSum = digits[0] + digits[2] + digits[4] + digits[6] + digits[8];
  const evenSum = digits[1] + digits[3] + digits[5] + digits[7];
  if ((oddSum * 7 - evenSum) % 10 !== digits[9]) {
    return false;
  }

  return true;
}

export const convertToCents = (price?: number) => {
  if (!price) return 0;
  return Math.round(price * 100);
};

export const convertToTL = (cents?: number): number => {
  if (!cents) return 0;
  return parseFloat((cents / 100).toFixed(2));
};

export const copyToClipboard = async (text: string) => {
  try {
    await navigator.clipboard.writeText(text);
    message.success('Kopyalandı!');
  } catch {
    message.error('Kopyalanırken bir hata oluştu!');
  }
};

export async function barcodePdfPrint({ zpl }: { zpl: string }) {
  const labelaryUrl = `https://api.labelary.com/v1/printers/8dpmm/labels/4x4/0/${zpl}`;

  const pdfBlob = await fetch(labelaryUrl, {
    method: 'GET',
    headers: {
      Accept: 'application/pdf',
      'Content-Type': 'application/x-www-form-urlencoded'
    }
  }).then((r) => {
    if (!r.ok) throw new Error('PDF oluşturma hatası');
    return r.blob();
  });

  const pdfURL = URL.createObjectURL(pdfBlob);

  const iframe = document.createElement('iframe');
  iframe.style.display = 'none';
  iframe.src = pdfURL;

  iframe.onload = () => {
    iframe.contentWindow?.print();
  };

  document.body.appendChild(iframe);
}

async function wait(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export async function barcodeMultiplePdfPrint({ zpls }: { zpls: string[] }) {
  if (zpls.length > 50) {
    // Split the array into chunks of 50

    const chunkedZpls = zpls.reduce((acc, zpl, index) => {
      const chunkIndex = Math.floor(index / 50);

      if (!acc[chunkIndex]) {
        acc[chunkIndex] = [];
      }

      acc[chunkIndex].push(zpl);

      return acc;
    }, [] as string[][]);

    for (const chunk of chunkedZpls) {
      if (chunk.length > 50) {
        throw new Error('ZPL sayısı 50 den fazla olamaz');
      }

      await barcodeMultiplePdfPrint({ zpls: chunk });
      await wait(1000);
    }
    return;
  }

  const labelaryUrl = `https://api.labelary.com/v1/printers/8dpmm/labels/4x4`;

  const pdfBlob = await fetch(labelaryUrl, {
    method: 'POST',
    headers: {
      Accept: 'application/pdf',
      'Content-Type': 'application/x-www-form-urlencoded'
    },
    body: zpls.join('\n')
  }).then((r) => {
    if (!r.ok) throw new Error('PDF oluşturma hatası');
    return r.blob();
  });

  const pdfURL = URL.createObjectURL(pdfBlob);

  const iframe = document.createElement('iframe');
  iframe.style.display = 'none';
  iframe.src = pdfURL;

  iframe.onload = () => {
    iframe.contentWindow?.print();
  };

  document.body.appendChild(iframe);
}

export function generateRandomHex(length: number = 8): string {
  const characters = '0123456789abcdef';
  let result = '';

  for (let i = 0; i < length; i++) {
    const randomIndex = Math.floor(Math.random() * characters.length);
    result += characters[randomIndex];
  }

  return result;
}

/**
 * Kriptografik güvenli hex üretir (webhook secret, imza anahtarı vb. için).
 * Varsayılan 32 bayt = 64 hex karakter = 256 bit. Math.random DEĞİL, crypto.
 */
export function generateSecureHex(bytes: number = 32): string {
  const arr = new Uint8Array(bytes);
  crypto.getRandomValues(arr);
  return Array.from(arr, (b) => b.toString(16).padStart(2, '0')).join('');
}

export function normalizeLatLng(a: number, b: number) {
  const isLat = (val: number) => val >= 36 && val <= 42;
  const isLng = (val: number) => val >= 26 && val <= 45;

  if (isLat(a) && isLng(b)) return { lat: a, lng: b };
  if (isLat(b) && isLng(a)) return { lat: b, lng: a };

  return { lat: undefined, lng: undefined };
}

export function scrollToFirstError(errorInfo: { errorFields: { name: (string | number)[]; errors: string[] }[] }) {
  if (errorInfo.errorFields.length > 0) {
    const firstErrorField = errorInfo.errorFields[0].name;
    const fieldName = Array.isArray(firstErrorField) ? firstErrorField.join('_') : firstErrorField;
    const fieldElement =
      document.querySelector(`[id="${fieldName}"]`) ||
      document.querySelector(`[id*="${fieldName}"]`) ||
      document.querySelector(`[name="${fieldName}"]`);

    if (fieldElement) {
      const formItem = fieldElement.closest('.ant-form-item');
      if (formItem) {
        // Önce mevcut shake class'ını kaldır (tekrar tetiklenebilmesi için)
        formItem.classList.remove('form-field-shake');

        formItem.scrollIntoView({ behavior: 'smooth', block: 'center' });

        // Scroll animasyonu tamamlandıktan sonra shake efektini başlat
        setTimeout(() => {
          formItem.classList.add('form-field-shake');
          setTimeout(() => {
            formItem.classList.remove('form-field-shake');
          }, 500);
        }, 300);

        // Focus on the input element
        if (fieldElement instanceof HTMLElement) {
          setTimeout(() => {
            fieldElement.focus();
          }, 350);
        }
      }
    }
  }
}
